import React, { useState, useEffect } from 'react';
import { clientsAPI, uploadAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Button } from '../ui';
import { handleApiError, getImageUrl } from '../../utils/helpers';

const EditClientModal = ({ client, isOpen, onClose, onSuccess }) => {
  const { isRTL } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    description: '',
    logo_url: '',
    phone: '',
    whatsapp_number: '',
    website: '',
    instagram: '',
    twitter: '',
    facebook: '',
    snapchat: '',
    tiktok: '',
    linkedin: '',
    youtube: '',
    store_platform: '',
    instagram_access_token: '',
    instagram_business_account_id: '',
    instagram_page_id: '',
  });

  useEffect(() => {
    if (client && isOpen) {
      setFormData({
        company_name: client.company_name || '',
        description: client.description || '',
        logo_url: client.logo_url || '',
        phone: client.phone || '',
        whatsapp_number: client.whatsapp_number || '',
        website: client.website || '',
        instagram: client.social_media_accounts?.instagram || '',
        twitter: client.social_media_accounts?.twitter || '',
        facebook: client.social_media_accounts?.facebook || '',
        snapchat: client.social_media_accounts?.snapchat || '',
        tiktok: client.social_media_accounts?.tiktok || '',
        linkedin: client.social_media_accounts?.linkedin || '',
        youtube: client.social_media_accounts?.youtube || '',
        store_platform: client.social_media_accounts?.store_platform || '',
        instagram_access_token: client.instagram_access_token || '',
        instagram_business_account_id: client.instagram_business_account_id || '',
        instagram_page_id: client.instagram_page_id || '',
      });
      setLogoPreview(client.logo_url ? getImageUrl(client.logo_url) : null);
      setLogoFile(null);
      setError(null);
    }
  }, [client, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.company_name.trim()) {
      setError(isRTL ? 'اسم الشركة مطلوب' : 'Company name is required');
      return;
    }

    setSubmitting(true);

    try {
      let logoUrl = formData.logo_url;

      // Upload logo file if selected
      if (logoFile) {
        const uploadResponse = await uploadAPI.uploadImage(logoFile);
        logoUrl = uploadResponse.data.url;
      }

      // Build social media accounts object (including store platform)
      const socialMediaAccounts = {};
      if (formData.instagram.trim()) socialMediaAccounts.instagram = formData.instagram.trim();
      if (formData.twitter.trim()) socialMediaAccounts.twitter = formData.twitter.trim();
      if (formData.facebook.trim()) socialMediaAccounts.facebook = formData.facebook.trim();
      if (formData.snapchat.trim()) socialMediaAccounts.snapchat = formData.snapchat.trim();
      if (formData.tiktok.trim()) socialMediaAccounts.tiktok = formData.tiktok.trim();
      if (formData.linkedin.trim()) socialMediaAccounts.linkedin = formData.linkedin.trim();
      if (formData.youtube.trim()) socialMediaAccounts.youtube = formData.youtube.trim();
      if (formData.store_platform) socialMediaAccounts.store_platform = formData.store_platform;

      const updateData = {
        company_name: formData.company_name.trim(),
        description: formData.description.trim() || null,
        logo_url: logoUrl || null,
        phone: formData.phone.trim() || null,
        whatsapp_number: formData.whatsapp_number.trim() || null,
        website: formData.website.trim() || null,
        social_media_accounts: Object.keys(socialMediaAccounts).length > 0 ? socialMediaAccounts : null,
        instagram_access_token: formData.instagram_access_token.trim() || null,
        instagram_business_account_id: formData.instagram_business_account_id.trim() || null,
        instagram_page_id: formData.instagram_page_id.trim() || null,
      };

      await clientsAPI.update(client.id, updateData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {isRTL ? 'تعديل بيانات العميل' : 'Edit Client'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isRTL ? 'معلومات الشركة' : 'Company Information'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'اسم الشركة' : 'Company Name'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    required
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'الوصف' : 'Description'}
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="textarea w-full"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'رقم الجوال' : 'Phone Number'}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'رقم واتساب' : 'WhatsApp Number'}
                  </label>
                  <input
                    type="tel"
                    name="whatsapp_number"
                    value={formData.whatsapp_number}
                    onChange={handleChange}
                    placeholder={isRTL ? 'مثال: 966501234567' : 'e.g., 966501234567'}
                    className="input w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL ? 'مع كود الدولة (للمشاركة المباشرة)' : 'With country code (for direct sharing)'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'الموقع الإلكتروني' : 'Website'}
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="input w-full"
                  />

                {/* Store Platform */}
                  <div className="mt-3">
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      {isRTL ? 'منصة المتجر' : 'Store Platform'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'zid', ar: 'زد', en: 'Zid' },
                        { id: 'salla', ar: 'سلة', en: 'Salla' },
                      { id: '3tech', ar: '3تك', en: '3Tech' },
                        { id: 'other', ar: 'منصة أخرى', en: 'Other' },
                      ].map((p) => {
                        const isSelected = formData.store_platform === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, store_platform: p.id })}
                            className={`
                              inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                              ${
                                isSelected
                                  ? 'border-sky-500 bg-sky-50 text-sky-700 shadow-sm'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-sky-200 hover:bg-sky-50'
                              }
                            `}
                          >
                            {isRTL ? p.ar : p.en}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'الشعار' : 'Logo'}
                  </label>
                  <div className="flex items-center gap-4">
                    {logoPreview && (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200"
                      />
                    )}
                    <div>
                      <label className="btn btn-secondary cursor-pointer">
                        {isRTL ? 'اختر صورة' : 'Choose Image'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoFileChange}
                          className="hidden"
                        />
                      </label>
                      {logoFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview(formData.logo_url ? getImageUrl(formData.logo_url) : null);
                          }}
                          className="text-sm text-red-600 hover:text-red-700 ml-2"
                        >
                          {isRTL ? 'إزالة' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL ? 'أو أدخل رابط الصورة:' : 'Or enter image URL:'}
                  </p>
                  <input
                    type="url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleChange}
                    className="input w-full mt-1"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isRTL ? 'حسابات التواصل الاجتماعي' : 'Social Media Accounts'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="@username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
                  <input
                    type="text"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="@username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Snapchat</label>
                  <input
                    type="text"
                    name="snapchat"
                    value={formData.snapchat}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TikTok</label>
                  <input
                    type="text"
                    name="tiktok"
                    value={formData.tiktok}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="@username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
                  <input
                    type="text"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">YouTube</label>
                  <input
                    type="text"
                    name="youtube"
                    value={formData.youtube}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="channel name"
                  />
                </div>
              </div>
            </div>

            {/* Instagram API Integration */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isRTL ? 'ربط Instagram API' : 'Instagram API Integration'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {isRTL 
                  ? 'إعدادات ربط Instagram Graph API للمشاركة المباشرة على Instagram'
                  : 'Configure Instagram Graph API settings for direct Instagram sharing'}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isRTL ? 'Access Token' : 'Access Token'} <span className="text-gray-400 text-xs">({isRTL ? 'مطلوب' : 'Required'})</span>
                  </label>
                  <input
                    type="password"
                    name="instagram_access_token"
                    value={formData.instagram_access_token}
                    onChange={handleChange}
                    className="input w-full font-mono text-xs"
                    placeholder="EAAx..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL 
                      ? 'Instagram Graph API Access Token مع الصلاحيات: instagram_basic, pages_show_list, pages_read_engagement'
                      : 'Instagram Graph API Access Token with permissions: instagram_basic, pages_show_list, pages_read_engagement'}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isRTL ? 'Business Account ID' : 'Business Account ID'} <span className="text-gray-400 text-xs">({isRTL ? 'مطلوب' : 'Required'})</span>
                    </label>
                    <input
                      type="text"
                      name="instagram_business_account_id"
                      value={formData.instagram_business_account_id}
                      onChange={handleChange}
                      className="input w-full font-mono text-xs"
                      placeholder="178414053092..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {isRTL ? 'Instagram Business Account ID (IG User ID)' : 'Instagram Business Account ID (IG User ID)'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isRTL ? 'Page ID' : 'Page ID'} <span className="text-gray-400 text-xs">({isRTL ? 'اختياري' : 'Optional'})</span>
                    </label>
                    <input
                      type="text"
                      name="instagram_page_id"
                      value={formData.instagram_page_id}
                      onChange={handleChange}
                      className="input w-full font-mono text-xs"
                      placeholder="1234567890..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {isRTL ? 'Facebook Page ID المرتبط بحساب Instagram' : 'Facebook Page ID connected to Instagram'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={submitting}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={submitting}
              >
                {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default EditClientModal;

